import React, { useState } from "react";
import { ArrowUp, ArrowDown, Fuel, Minus } from "lucide-react";

const DynamicProgressBar = ({
  value,
  maxValue,
  color,
  backgroundColor,
  unit = "MT",
  height = "h-8",
  borderRadius = "rounded-lg",
}) => {
  const percentage = Math.min((value / maxValue) * 100, 100);

  return (
    <div
      className={`relative ${height} ${backgroundColor} ${borderRadius} overflow-hidden mb-4 shadow-sm`}
    >
      {/* Progress fill */}
      <div
        className={`absolute top-1.5 h-5 ${borderRadius} transition-all duration-1000 ease-out`}
        style={{
          width: `${percentage}%`,
          backgroundColor: color,
        }}
      >
        {/* Light streak animation */}
        <div
          className="absolute inset-0"
          style={{
            width: "40%",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%)",
            transform: "skewX(-20deg)",
            animation: "lightStreak 2.5s ease-in-out infinite",
          }}
        />
      </div>

      {/* Value label */}
      <div className="absolute inset-0 flex items-center justify-end pr-3">
        <span
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "#000000", // Always black
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {value} {unit}
        </span>
      </div>
    </div>
  );
};

const APFuelConsumption = (fuelData) => {
  const beforeValue = fuelData.fuelData?.before || 0;
  const afterValue = fuelData.fuelData?.after || 0;
  const [unit, setUnit] = useState("MT");
  
  // Handle NaN and zero division cases
  let changeValue = 0;
  if (beforeValue > 0 && !isNaN(beforeValue) && !isNaN(afterValue)) {
    changeValue = ((afterValue - beforeValue) / beforeValue) * 100;
  }
  
  const isPositive = afterValue > beforeValue;
  const isValidChange = !isNaN(changeValue) && isFinite(changeValue);

  const maxValue = Math.max(beforeValue, afterValue, 100);

  return (
    <div className="flex flex-col h-full p-4 bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center mb-6">
        <div className="p-2 bg-[#d354001a] rounded-lg mr-3">
          <Fuel size={20} className="text-[#d35400]" />
        </div>
        <div>
          <h3 className="font-bold text-slate-800">Fuel Consumption</h3>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col gap-5 mb-5 mt-5">
        {/* Before and After Labels */}
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full mr-2 bg-[#1a5276]"></div>
            <span className="text-sm font-medium text-slate-600">Before</span>
          </div>

          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full mr-2 bg-[#27ae60]"></div>
            <span className="text-sm font-medium text-slate-600">After</span>
          </div>
        </div>

        {/* Progress bars */}
        <div className="space-y-7">
          <DynamicProgressBar
            value={beforeValue}
            maxValue={maxValue}
            color="#1a5276"
            backgroundColor="bg-[#1a53761a]"
            textColor="#1a5276"
            unit={unit}
          />
          <DynamicProgressBar
            value={afterValue}
            maxValue={maxValue}
            color="#27ae60"
            backgroundColor="bg-[#27ae601a]"
            textColor="#27ae60"
            unit={unit}
          />
        </div>
      </div>

      {/* Change Indicator */}
      <div className="pt-3 border-t border-slate-100 flex-1">
        <div className={`flex items-center justify-center gap-1 p-2 rounded-lg ${
          isValidChange ? (isPositive ? 'bg-[#1a52761a]' : 'bg-[#27ae601a]') : 'bg-slate-100'
        }`}>
          {isValidChange ? (
            isPositive ? (
              <ArrowUp size={20} className="text-[#1a5276]" />
            ) : (
              <ArrowDown size={20} className="text-[#27ae60]" />
            )
          ) : (
            <Minus size={20} className="text-slate-400" />
          )}
          <span className={`text-[16px] font-bold ${
            isValidChange 
              ? (isPositive ? 'text-[#1a5276]' : 'text-[#27ae60]') 
              : 'text-slate-500'
          }`}>
            {isValidChange ? Math.abs(changeValue.toFixed(1)) : 0}% {isValidChange ? (isPositive ? 'increase' : 'decrease') : 'no change'}
          </span>
        </div>
      </div>

      <style jsx>{`
        @keyframes lightStreak {
          0% {
            transform: translateX(-100%) skewX(-20deg);
          }
          50% {
            transform: translateX(200%) skewX(-20deg);
          }
          100% {
            transform: translateX(-100%) skewX(-20deg);
          }
        }
      `}</style>
    </div>
  );
};

export default APFuelConsumption;